import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import CoursesList from "@/pages/CoursesList";
import CourseDetail from "@/pages/CourseDetail";
import ModuleEditor from "@/pages/ModuleEditor";
import LearnersPage from "@/pages/LearnersPage";
import ProgressPage from "@/pages/ProgressPage";
import QuizLibrary from "@/pages/QuizLibrary";
import LearnerHome from "@/pages/LearnerHome";
import LearnerCourse from "@/pages/LearnerCourse";
import LearnerModule from "@/pages/LearnerModule";
import PublicCoursePreview from "@/pages/PublicCoursePreview";
import PublicModulePreview from "@/pages/PublicModulePreview";
import DemoEntry from "@/pages/DemoEntry";
import { SimulatorGallery, SimulatorChat } from "@/pages/Simulator";
import { ForumIndex, ForumThread } from "@/pages/Forum";
import Feedback from "@/pages/Feedback";
import AdminFeedback from "@/pages/AdminFeedback";
import CertificatePage from "@/pages/CertificatePage";
import CertificateVerify from "@/pages/CertificateVerify";
import ResetPassword from "@/pages/ResetPassword";
import ProtectedRoute from "@/components/ProtectedRoute";
import { getUser } from "@/lib/api";

function RootRedirect() {
  const u = getUser();
  if (!u) return <Navigate to="/login" replace />;
  return <Navigate to={u.role === "admin" ? "/admin" : "/learn"} replace />;
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />

          {/* Admin routes */}
          <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/courses" element={<ProtectedRoute role="admin"><CoursesList /></ProtectedRoute>} />
          <Route path="/admin/courses/:courseId" element={<ProtectedRoute role="admin"><CourseDetail /></ProtectedRoute>} />
          <Route path="/admin/modules/:moduleId" element={<ProtectedRoute role="admin"><ModuleEditor /></ProtectedRoute>} />
          <Route path="/admin/learners" element={<ProtectedRoute role="admin"><LearnersPage /></ProtectedRoute>} />
          <Route path="/admin/progress" element={<ProtectedRoute role="admin"><ProgressPage /></ProtectedRoute>} />
          <Route path="/admin/quizzes" element={<ProtectedRoute role="admin"><QuizLibrary /></ProtectedRoute>} />
          <Route path="/admin/feedback" element={<ProtectedRoute role="admin"><AdminFeedback /></ProtectedRoute>} />

          {/* Learner routes (admin can also access for preview) */}
          <Route path="/learn" element={<ProtectedRoute><LearnerHome /></ProtectedRoute>} />
          <Route path="/learn/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
          <Route path="/learn/certificate" element={<ProtectedRoute><CertificatePage /></ProtectedRoute>} />
          <Route path="/reset-password" element={<ProtectedRoute><ResetPassword /></ProtectedRoute>} />
          <Route path="/learn/simulator" element={<ProtectedRoute><SimulatorGallery /></ProtectedRoute>} />
          <Route path="/learn/simulator/:sessionId" element={<ProtectedRoute><SimulatorChat /></ProtectedRoute>} />
          <Route path="/learn/forum" element={<ProtectedRoute><ForumIndex /></ProtectedRoute>} />
          <Route path="/learn/forum/:threadId" element={<ProtectedRoute><ForumThread /></ProtectedRoute>} />
          <Route path="/learn/courses/:courseId" element={<ProtectedRoute><LearnerCourse /></ProtectedRoute>} />
          <Route path="/learn/modules/:moduleId" element={<ProtectedRoute><LearnerModule /></ProtectedRoute>} />

          {/* Public share preview (no auth) — long-lived per-course links */}
          <Route path="/share/c/:token" element={<PublicCoursePreview />} />
          <Route path="/share/c/:token/m/:moduleId" element={<PublicModulePreview />} />

          {/* Magic demo link — auto-signs visitor in as a demo learner for one course */}
          <Route path="/demo/:token" element={<DemoEntry />} />

          {/* Public certificate verification (no auth) — the LinkedIn-shared URL */}
          <Route path="/certificate/:certId" element={<CertificateVerify />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
